import { ref } from 'vue';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Browser } from '@capacitor/browser';
import { Share } from '@capacitor/share';
import { getMimeType, getFileName } from '@/helpers/files';
import { useLoading } from '@/composables/useLoading';
import { useSnackBar } from '@/composables/useSnackBar';

export type DownloadResult = {
  uri: string;
  fileName: string;
  mimeType: string;
};

export function useFileOpener() {
  const { startLoader, dismissLoader } = useLoading();
  const snackBar = useSnackBar();
  const error = ref<string | null>(null);

  const downloadFileWeb = async (
    url: string,
    fileName: string,
    mimeType: string,
  ): Promise<DownloadResult | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      return { uri: url, fileName, mimeType };
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to download file';
      console.error('Download error:', e);
      return null;
    }
  };

  const downloadFileNative = async (
    url: string,
    fileName: string,
    mimeType: string,
  ): Promise<DownloadResult | null> => {
    try {
      const result = await Filesystem.downloadFile({
        url,
        path: fileName,
        directory: Directory.Documents,
        recursive: true,
      });

      await Share.share({
        title: fileName,
        url: result.path!,
      });

      return { uri: result.path!, fileName, mimeType };
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to download file';
      console.error('Download error:', e);
      return null;
    }
  };

  const downloadFile = async (
    url: string,
    options?: { fileName?: string },
  ): Promise<DownloadResult | null> => {
    await startLoader();
    error.value = null;

    if (!url) {
      error.value = 'No file URL provided';
      await dismissLoader();
      return null;
    }

    const fileName = options?.fileName || getFileName(url);
    const mimeType = getMimeType(url);

    let result: DownloadResult | null = null;

    if (!Capacitor.isNativePlatform()) {
      result = await downloadFileWeb(url, fileName, mimeType);
    } else {
      result = await downloadFileNative(url, fileName, mimeType);
      if (!result) {
        await Browser.open({ url });
      }
    }

    await dismissLoader();

    if (!result) {
      await snackBar.show({
        text: 'Échec du téléchargement du fichier',
      });
    }

    return result;
  };

  const openFile = async (url: string): Promise<void> => {
    if (!url) {
      error.value = 'No file URL provided';
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      window.open(url, '_blank');
      return;
    }

    error.value = null;

    try {
      const downloadResult = await downloadFile(url);
      if (!downloadResult) {
        return;
      }

      await FileOpener.open({
        filePath: downloadResult.uri,
        contentType: downloadResult.mimeType,
      });
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to open file';
      console.error('File opener error:', e);
    }
  };

  const shareFile = async (url: string): Promise<void> => {
    if (!url) {
      error.value = 'No file URL provided';
      return;
    }

    error.value = null;

    try {
      await Share.share({
        title: getFileName(url),
        url,
      });
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to share file';
      console.error('Share error:', e);
    }
  };

  return {
    error,
    openFile,
    downloadFile,
    shareFile,
  };
}
